import {
  labeebAuditListPath,
  labeebEventsOf,
  labeebNextCursor,
  type LabeebAuditDomain,
  type LabeebAuditPage,
} from "../api/labeeb";
import {
  auditListPath,
  eventsOf,
  type AuditDomain,
  type AuditPage,
} from "../api/mcp";
import type { AppConfig } from "../config";
import { usePagedList } from "./usePagedList";

/**
 * Each product keeps its OWN audit table. This picks which one is being read.
 *
 * They are not the same shape. MCP sends `events`, `cursor`, `timestamp` and
 * `actor`. Labeeb sends `items`, `next_cursor`, `at` and `operator.email`, and
 * only MCP's fleet list takes a day window. One hook still serves both because
 * the path and the row reader come from the product's own module — this file
 * only chooses which product.
 */
export type AuditProduct = "mcp" | "labeeb";

/** The domain filter, whichever product's vocabulary it is drawn from. */
export type AnyAuditDomain = AuditDomain | LabeebAuditDomain | "";

/**
 * A row from EITHER product's audit table.
 *
 * `domain` widens to `string` here on purpose. MCP types it as its own union
 * (`tenant | quota | settings`) and Labeeb's is a different set
 * (`tenant | quota | flags | config`) — so a row type that kept either union
 * would make the other product's rows a compile error, and casting between
 * them would be a lie the renderer then has to survive. Everything a shared
 * renderer actually needs is common to both, and the parts that are not —
 * where a target links to — stay behind a product check at the call site.
 */
export interface AnyAuditEvent {
  domain: string;
  action: string;
  actor: string | null;
  target: unknown;
  before: unknown;
  after: unknown;
  timestamp: string | null;
  /**
   * Why the operator said they made the change. Common to both products —
   * MCP stores it when the write sent one, Labeeb requires it on every
   * change — so the shared renderer can show it.
   *
   * Optional: old rows predate the field, and a few MCP writes take no
   * reason. Absent is rendered as a dash, never as an empty cell.
   */
  reason?: string | null;
}

/**
 * The fleet audit list, paged through `usePagedList`.
 *
 * Each product builds its own path: only MCP's fleet list takes a day window
 * (Labeeb's audit takes limit and cursor only), and a domain valid for one is
 * a 422 for the other. Each product's own total helper reads the rows, so a
 * truncated response degrades through that product's floor: MCP sends
 * `events`/`cursor`, Labeeb `items`/`next_cursor`.
 */
export function useAuditFeed(
  config: AppConfig,
  domain: AnyAuditDomain,
  product: AuditProduct = "mcp",
  days = 7,
) {
  const labeeb = product === "labeeb";
  const path = labeeb
    ? labeebAuditListPath(domain as LabeebAuditDomain | "")
    : auditListPath(domain as AuditDomain | "", days);
  return usePagedList<AnyAuditEvent>(config, path, (page) =>
    labeeb
      ? {
          rows: labeebEventsOf(page as LabeebAuditPage),
          next: labeebNextCursor(page as LabeebAuditPage),
        }
      : { rows: eventsOf(page as AuditPage), next: (page as AuditPage | null)?.cursor ?? null },
  );
}
